<template>
  <EquipmentBase
    ref="wrapper"
    :section-id="ScenarioDetailSectionKey.PUMP"
    :class="{ 'compact-mode': !!stack }"
  >
    <!-- Compact Mode Layout -->
    <template v-if="stack">
      <!-- Header Row: Pump Title (left) | Pump Curve Toggle (right) -->
      <div class="compact-header-row" style="margin-top: 0.75rem">
        <div class="flex align-items-center">
          <h3 class="compact-title">Pump</h3>
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.PUMP]"
          />
        </div>
        <div class="flex align-items-center">
          <LabelledToggle v-model="showCurve" text="Pump Curve" />
        </div>
      </div>

      <!-- Pump Curve Chart - Right below header -->
      <transition name="vertical-slide">
        <SinglePumpChart
          v-if="showCurve"
          draw-legend
          enable-mouse-tracking
          hide-pump-selector
          :minimum-height-px="300"
          :aspect-ratio="10"
          :npsh-mode="!isEspOnShore"
        />
      </transition>

      <!-- Checkboxes Row: Compression Pump | Include Pressure Drop -->
      <div
        v-if="isEspOnShore"
        class="compact-pump-checkboxes-row flex flex-wrap gap-4"
      >
        <div class="p-field-checkbox flex align-items-center">
          <Checkbox
            :input-id="`compression-pump-checkbox-${stack}`"
            :model-value="useCompression"
            :disabled="
              isScenarioReadonly ||
              isImportedFromLegacy ||
              containsCompressionPump
            "
            binary
            @update:model-value="useCompression = $event"
          />
          <label
            :for="`compression-pump-checkbox-${stack}`"
            class="compact-label ml-1"
            >Compression Pump</label
          >
        </div>
        <div
          v-tooltip.top="casingFrictionLossTooltipText"
          class="p-field-checkbox flex align-items-center"
        >
          <Checkbox
            :input-id="`casing-friction-loss-checkbox-${stack}`"
            :model-value="includeCasingFrictionLoss"
            :disabled="isScenarioReadonly || isImportedFromLegacy"
            binary
            @update:model-value="includeCasingFrictionLoss = $event"
          />
          <label
            :for="`casing-friction-loss-checkbox-${stack}`"
            class="compact-label ml-1"
            >Include Friction Losses Past Equipment</label
          >
        </div>
      </div>

      <Divider />

      <!-- Motor Poles Frequency (if applicable) -->
      <div
        v-if="scenarioCalculations.motorCalculations.motorPoles.value > 2"
        class="mb-2"
      >
        <InlineValue
          :value="scenarioCalculations.adjustedPumpHzFrequency.value.toFixed(2)"
          units="Hz"
          :title="`${scenarioCalculations.motorCalculations.motorPoles.value} Pole Frequency`"
        />
      </div>

      <!-- Hz Input and Pump Wear Corrections Row -->
      <div class="flex flex-wrap gap-2 align-items-center mb-2">
        <ConverterInput
          label=""
          :model-value="pumpHz"
          :base-unit="FrequencyUnits.Hertz"
          :model="FrequencyUnits.options()"
          mode="write"
          :decimal-places="6"
          :rule-names="['vsd_freq_low', 'vsd_freq_high', 'switchboard_freq']"
          :class="[
            'compact-hz-input',
            { 'calc-hz-stale-input': isCalcHzStale }
          ]"
          @update:model-value="handlePumpHzInput"
        />
        <ConverterInput
          label="Pump Wear Head Corr."
          :model-value="pumpWearHeadCorrectionRatio"
          :base-unit="PercentageUnits.Ratio"
          :target-unit="PercentageUnits.Percentage"
          :model="PercentageUnits.options()"
          mode="write"
          :min="0"
          class="compact-correction-input"
          @update:model-value="handlePumpWearHeadInput"
        />
        <ConverterInput
          label="Pump Wear BHP Corr."
          :model-value="pumpWearBhpCorrectionRatio"
          :base-unit="PercentageUnits.Ratio"
          :target-unit="PercentageUnits.Percentage"
          :model="PercentageUnits.options()"
          mode="write"
          :min="0"
          class="compact-correction-input"
          @update:model-value="handlePumpWearBhpInput"
        />
      </div>

      <!-- Calc Buttons Row -->
      <div class="flex flex-wrap gap-2 align-items-center mb-2">
        <div>
          <div
            v-tooltip.hover.top="predictionErrorTooltipData"
            :class="severityClass"
          >
            <Button
              label="Calc Hz"
              :class="
                predictionErrors.length > 0
                  ? 'p-button-danger'
                  : 'p-button p-component'
              "
              :disabled="disableCalcHzButton"
              @click="calcHz"
            />
          </div>
        </div>
        <div v-if="isEspOnShore">
          <div
            v-tooltip.hover.top="predictionErrorTooltipData"
            :class="severityClass"
          >
            <Button
              :class="predictionErrors.length > 0 ? 'p-button-danger' : ''"
              label="Calc Flow"
              :disabled="disableCalcHzButton"
              @click="calcFlow"
            />
          </div>
        </div>
      </div>
      <div
        v-if="isCalcHzStale && !isEspOnShore"
        class="calc-hz-stale-warning mb-2"
      >
        <i class="pi pi-exclamation-triangle" />
        <span>
          Calc Hz may be outdated after recent changes. Click
          <strong>Calc Hz</strong> to refresh.
        </span>
      </div>

      <!-- Gas Head Rerate Fields (ESP OnShore only) -->
      <div v-if="isEspOnShore" class="flex flex-wrap gap-4 mb-2">
        <div class="p-field-checkbox flex align-items-center">
          <Checkbox
            :input-id="`legacy-gas-head-rerate-checkbox-${stack}`"
            :model-value="useLegacyGasHeadRerate"
            :disabled="isScenarioReadonly || isImportedFromLegacy"
            binary
            @update:model-value="useLegacyGasHeadRerate = $event"
          />
          <label
            :for="`legacy-gas-head-rerate-checkbox-${stack}`"
            class="compact-label ml-1"
          >
            Use Legacy Gas Head Rerate
          </label>
        </div>
      </div>
      <div v-if="isEspOnShore" class="flex flex-wrap gap-4 mb-2">
        <ConverterInput
          label="Override Gas Head Rerate"
          :model-value="
            useLegacyGasHeadRerate
              ? Ratio.toStandardUnit(
                  Math.round(
                    scenarioCalculations.pumpCalculations.aggregate
                      .gasHeadRerateRatio.value * 10000
                  ) / 10000
                )
              : pumpOverrideGasCorrectionPercent
          "
          mode="write"
          :base-unit="PercentageUnits.Percentage"
          :model="PercentageUnits.options()"
          :disabled="
            isScenarioReadonly ||
            isImportedFromLegacy ||
            !!useLegacyGasHeadRerate
          "
          class="compact-correction-input"
          @update:model-value="pumpOverrideGasCorrectionPercent = $event"
        />
        <ConverterInput
          label="Gas Head Rerate"
          mode="read"
          :model-value="gasHeadRerateRatioForStack"
          :base-unit="PercentageUnits.Ratio"
          :target-unit="PercentageUnits.Percentage"
          :model="PercentageUnits.options()"
          class="compact-correction-input"
        />
      </div>

      <Divider />

      <BoltOnDischargeRow
        v-if="stack !== 'lower'"
        :stack="stack"
        :is-hps="isHpsScenario"
      />

      <!-- Pump Rows -->
      <div v-if="pumpsOrder?.length > 0" class="space-y-3">
        <template v-for="(pumpXrefId, i) in pumpsOrder" :key="pumpXrefId">
          <UsePumpRowHeader
            v-slot="{ header }"
            :xref-pump="getPump(pumpXrefId)"
            :order="pumpsOrderXrefs"
          >
            <PumpRow
              :pump="getPump(pumpXrefId)"
              :pump-index="i"
              :total-pumps="pumpsOrder?.length || 0"
              :hide-add-above="newPumpExists"
              :hide-add-below="newPumpExists"
              :hide-remove="false"
              :panel-header="header"
              :has-compression="!!hasCompressionPump"
              :stack="stack"
              @add-above="addPump(i)"
              @add-below="addPump(i + 1)"
              @remove="removePump($event, i)"
              @update:pumps="updatePump($event, i)"
              @auto-add-hz-applied="handleAutoAddHzApplied"
              @auto-add-started="handleAutoAddStarted"
              @auto-add-finished="handleAutoAddFinished"
            />
          </UsePumpRowHeader>
        </template>
      </div>
      <div v-else class="mb-3">
        <PumpRow
          :pump="initialPumpXref"
          :pump-index="0"
          :total-pumps="1"
          :panel-header="`Primary Pump`"
          :hide-add-above="true"
          :hide-add-below="true"
          :hide-remove="false"
          :has-compression="!!hasCompressionPump"
          :stack="stack"
          @update:pumps="updatePump($event, 0)"
          @auto-add-hz-applied="handleAutoAddHzApplied"
          @auto-add-started="handleAutoAddStarted"
          @auto-add-finished="handleAutoAddFinished"
        />
      </div>
    </template>

    <!-- Full Mode Layout (default) -->
    <template v-else>
      <div v-if="isEspOnShore" class="flex flex-wrap mx-list-4">
        <div class="p-field-checkbox flex align-items-center">
          <Checkbox
            input-id="compression-pump-checkbox"
            :model-value="useCompression"
            :disabled="
              isScenarioReadonly ||
              isImportedFromLegacy ||
              containsCompressionPump
            "
            binary
            @update:model-value="useCompression = $event"
          />
          <label for="compression-pump-checkbox" class="ml-1"
            >Compression Pump</label
          >
        </div>
        <div
          v-tooltip.top="casingFrictionLossTooltipText"
          class="p-field-checkbox flex align-items-center"
        >
          <Checkbox
            input-id="casing-friction-loss-checkbox"
            :model-value="includeCasingFrictionLoss"
            :disabled="isScenarioReadonly || isImportedFromLegacy"
            binary
            @update:model-value="includeCasingFrictionLoss = $event"
          />
          <label for="casing-friction-loss-checkbox" class="ml-1"
            >Include Friction Losses Past Equipment</label
          >
        </div>
      </div>
      <EquipmentHeader>
        <template #title>
          <div class="flex align-items-center justify-content-between">
            Pump
            <CalculatorToggle
              :section-ids="[EquipmentCalculatorSectionKey.PUMP]"
            />
          </div>
        </template>
        <template #extra>
          <div class="flex mt-2 justify-content-start align-items-center">
            <ConverterInput
              label=""
              :model-value="pumpHz"
              :base-unit="FrequencyUnits.Hertz"
              :model="FrequencyUnits.options()"
              mode="write"
              :decimal-places="6"
              :rule-names="[
                'vsd_freq_low',
                'vsd_freq_high',
                'switchboard_freq'
              ]"
              :class="{ 'calc-hz-stale-input': isCalcHzStale }"
              @update:model-value="handlePumpHzInput"
            />
            <div class="col-3">
              <div
                v-tooltip.hover.top="predictionErrorTooltipData"
                :class="severityClass"
              >
                <Button
                  label="Calc Hz"
                  :class="
                    predictionErrors.length > 0
                      ? 'p-button-danger w-full'
                      : 'p-button p-component w-full'
                  "
                  :disabled="disableCalcHzButton"
                  @click="calcHz"
                />
              </div>
            </div>
            <div class="col-3">
              <div
                v-if="isEspOnShore"
                v-tooltip.hover.top="predictionErrorTooltipData"
                :class="severityClass"
              >
                <Button
                  class="w-full"
                  :class="
                    predictionErrors.length > 0
                      ? 'p-button-danger w-full'
                      : 'w-full'
                  "
                  label="Calc Flow"
                  :disabled="disableCalcHzButton"
                  @click="calcFlow"
                />
              </div>
            </div>
          </div>
        </template>
        <template #titleRight>
          <div class="col-fixed pump-stages toggle">
            <LabelledToggle v-model="showCurve" text="Pump Curve" />
          </div>
        </template>
      </EquipmentHeader>
      <div
        v-if="isCalcHzStale && !isEspOnShore"
        class="calc-hz-stale-warning ml-8 mb-2"
      >
        <i class="pi pi-exclamation-triangle" />
        <span>
          Calc Hz may be outdated after recent changes. Click
          <strong>Calc Hz</strong> to refresh.
        </span>
      </div>
      <div
        v-if="scenarioCalculations.motorCalculations.motorPoles.value > 2"
        class="flex pl-8 ml-5 pb-1 justify-content-between"
      >
        <InlineValue
          :value="scenarioCalculations.adjustedPumpHzFrequency.value.toFixed(2)"
          units="Hz"
          :title="`${scenarioCalculations.motorCalculations.motorPoles.value} Pole Frequency`"
          class="col-fixed"
        />
      </div>
      <transition name="vertical-slide">
        <SinglePumpChart
          v-if="showCurve"
          draw-legend
          enable-mouse-tracking
          hide-pump-selector
          :npsh-mode="!isEspOnShore"
          :minimum-height-px="300"
          :aspect-ratio="10"
        />
      </transition>

      <div class="flex mt-2 justify-content-between align-items-center">
        <div
          v-if="isEspOnShore"
          class="flex align-items-center mx-list-2 mobile-wrap pr-3"
        >
          <ConverterInput
            label="Pump Wear Head Correction"
            :model-value="pumpWearHeadCorrectionRatio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            :min="0"
            @update:model-value="handlePumpWearHeadInput"
          />
          <ConverterInput
            label="Pump Wear BHP Correction"
            :model-value="pumpWearBhpCorrectionRatio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            :min="0"
            @update:model-value="handlePumpWearBhpInput"
          />
        </div>
        <div
          v-if="isEspOnShore"
          class="flex align-items-center mx-list-2 mobile-wrap"
        >
          <div class="p-field-checkbox flex align-items-center">
            <Checkbox
              input-id="legacy-gas-head-rerate-checkbox"
              :model-value="useLegacyGasHeadRerate"
              :disabled="isScenarioReadonly || isImportedFromLegacy"
              binary
              @update:model-value="useLegacyGasHeadRerate = $event"
            />
            <label for="legacy-gas-head-rerate-checkbox" class="ml-1">
              Use Legacy Gas Head Rerate
            </label>
          </div>
          <ConverterInput
            label="Override Gas Head Rerate"
            :model-value="
              useLegacyGasHeadRerate
                ? Ratio.toStandardUnit(
                    Math.round(gasHeadRerateRatioForStack * 10000) / 10000
                  )
                : pumpOverrideGasCorrectionPercent
            "
            mode="write"
            :base-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            :disabled="
              isScenarioReadonly ||
              isImportedFromLegacy ||
              !!useLegacyGasHeadRerate
            "
            @update:model-value="pumpOverrideGasCorrectionPercent = $event"
          />
          <ConverterInput
            label="Gas Head Rerate"
            mode="read"
            :model-value="gasHeadRerateRatioForStack"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
          />
        </div>
      </div>

      <Divider />
      <BoltOnDischargeRow
        v-if="stack !== 'lower'"
        :stack="stack"
        :is-hps="isHpsScenario"
      />
      <div v-if="pumpsOrder?.length > 0" class="space-y-3">
        <template v-for="(pumpXrefId, i) in pumpsOrder" :key="pumpXrefId">
          <UsePumpRowHeader
            v-slot="{ header }"
            :xref-pump="getPump(pumpXrefId)"
            :order="pumpsOrderXrefs"
          >
            <PumpRow
              :pump="getPump(pumpXrefId)"
              :pump-index="i"
              :total-pumps="pumpsOrder?.length || 0"
              :hide-add-above="newPumpExists"
              :hide-add-below="newPumpExists"
              :hide-remove="false"
              :panel-header="header"
              :has-compression="!!hasCompressionPump"
              :stack="stack"
              @add-above="addPump(i)"
              @add-below="addPump(i + 1)"
              @remove="removePump($event, i)"
              @update:pumps="updatePump($event, i)"
              @auto-add-hz-applied="handleAutoAddHzApplied"
              @auto-add-started="handleAutoAddStarted"
              @auto-add-finished="handleAutoAddFinished"
            />
          </UsePumpRowHeader>
        </template>
      </div>
      <div v-else class="mb-3">
        <PumpRow
          :pump="generatePumpXref()"
          :pump-index="0"
          :total-pumps="1"
          :panel-header="`Primary Pump`"
          :hide-add-above="true"
          :hide-add-below="true"
          :hide-remove="false"
          :has-compression="!!hasCompressionPump"
          :stack="stack"
          @update:pumps="updatePump($event, 0)"
          @auto-add-hz-applied="handleAutoAddHzApplied"
          @auto-add-started="handleAutoAddStarted"
          @auto-add-finished="handleAutoAddFinished"
        />
      </div>
    </template>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect, nextTick, watch } from "vue";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import InlineValue from "@/components/scenarios/InlineValue.vue";
import PumpRow from "@/components/equipment/pumps/PumpRow.vue";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import { v4 } from "uuid";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  housingsFormKey,
  parametersFormKey,
  pumpsFormKey,
  scenarioKey,
  scenarioFormKey,
  scenarioCalculationsKey,
  scenarioValidatorKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { XrefHousings, XrefPumps } from "@/composables/forms/formTypes";
import { useRouter } from "vue-router";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import SinglePumpChart from "@/components/scenarios/charts/SinglePumpChart.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { PercentageUnits, Ratio } from "@/libs/units/percentage";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { FrequencyUnits } from "@/libs/units/frequency";
import { useGenericNotification } from "@/composables/logger/genericNotification";
import UsePumpRowHeader from "@/composables/equipment/pumps/UsePumpRowHeader.vue";
import BoltOnDischargeRow from "@/components/equipment/bolt_on_discharges/BoltOnDischargeRow.vue";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Divider from "primevue/divider";
import type {
  HypotheticalFlowDataRow,
  HypotheticalFlowResultStatus
} from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import { getPredictedDesiredFlowRateSTBD } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import type {
  HypotheticalFrequencyDataRow,
  HypotheticalFrequencyResultStatus
} from "@/libs/calculations/predictiveCalcs/getPredictedDesiredPumpHz";
import { getPredictedDesiredFrequencyHz } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredPumpHz";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { convert } from "@/libs/units";
import { PoundsPerSquareInchAbsolute } from "@/libs/units/pressure";
import { Sizing_Pumping_Type_Enum } from "gql/api";
// import { useStackCalculations } from "@/composables/scenarioDetailProvider/useStackCalculations";
// import { useStackValidator } from "@/composables/scenarioDetailProvider/useStackValidator";
// import type { UseScenarioValidator } from "@/composables/validation/scenarioValidator";
// import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import {
  getPumpFieldName,
  getPumpFieldValue
} from "@/composables/forms/stackAwareFields";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  disabled?: boolean;
  stack?: StackLocation;
}>();

const emit = defineEmits<{
  (e: "update:disabled", value: boolean): void;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);
const pumpsForm = injectStrict(pumpsFormKey);
const pumps = computed(() => pumpsForm.data.value);
const housingsForm = injectStrict(housingsFormKey);
const housings = computed(() => housingsForm.data.value);
const scenario = injectStrict(scenarioKey);
const scenarioForm = injectStrict(scenarioFormKey);

const { fireToast } = useGenericNotification();

// Use stack-specific calculations (access properties directly from .value, no extra wrapping)
// const stackCalculations = useStackCalculations(props.stack || "upper");
// const scenarioCalculations = computed(
//   () => stackCalculations.value as ScenarioCalculations
// );
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
// Use stack-aware validator
// const stackValidator = useStackValidator(
//   props.stack || "upper"
// ) as UseScenarioValidator;
// const { errors } = stackValidator;
const { errors } = injectStrict(scenarioValidatorKey);

const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

// Stack-aware field accessors for pump parameters
const pumpHzField = computed(() => getPumpFieldName("pumpHz", props.stack));
const hasCompressionPumpField = computed(() =>
  getPumpFieldName("hasCompressionPump", props.stack)
);
const usePressureDropPastMotorField = computed(() =>
  getPumpFieldName("includeCasingFrictionLoss", props.stack)
);
const pumpOverrideGasCorrectionPercentField = computed(() =>
  getPumpFieldName("pumpOverrideGasCorrectionPercent", props.stack)
);
const pumpWearHeadCorrectionRatioField = computed(() =>
  getPumpFieldName("pumpWearHeadCorrectionRatio", props.stack)
);
const pumpWearBhpCorrectionRatioField = computed(() =>
  getPumpFieldName("pumpWearBhpCorrectionRatio", props.stack)
);
const useLegacyGasHeadRerateField = computed(() =>
  getPumpFieldName("useLegacyGasHeadRerate", props.stack)
);

// Stack-aware computed values
const pumpHz = computed({
  get: () =>
    (getPumpFieldValue(parameters.value, "pumpHz", props.stack) as
      | number
      | null
      | undefined) ?? null,
  set: (value: number | null) => {
    parametersForm.update({
      [pumpHzField.value]: value
    } as Partial<typeof parametersForm.data.value>);
  }
});

const hasCompressionPump = computed({
  get: () =>
    (getPumpFieldValue(parameters.value, "hasCompressionPump", props.stack) as
      | boolean
      | null
      | undefined) ?? false,
  set: (value: boolean) => {
    parametersForm.update({
      [hasCompressionPumpField.value]: value
    } as Partial<typeof parametersForm.data.value>);
  }
});

const includeCasingFrictionLoss = computed({
  get: () =>
    (getPumpFieldValue(
      parameters.value,
      "includeCasingFrictionLoss",
      props.stack
    ) as boolean | null | undefined) ?? true,
  set: (value: boolean) => {
    parametersForm.update({
      [usePressureDropPastMotorField.value]: value
    } as Partial<typeof parametersForm.data.value>);
  }
});

const pumpOverrideGasCorrectionPercent = computed({
  get: () =>
    (getPumpFieldValue(
      parameters.value,
      "pumpOverrideGasCorrectionPercent",
      props.stack
    ) as number | null | undefined) ?? null,
  set: (value: number | null) => {
    parametersForm.update({
      [pumpOverrideGasCorrectionPercentField.value]: value
    } as Partial<typeof parametersForm.data.value>);
  }
});

const pumpWearHeadCorrectionRatio = computed({
  get: () =>
    (getPumpFieldValue(
      parameters.value,
      "pumpWearHeadCorrectionRatio",
      props.stack
    ) as number | null | undefined) ?? 1,
  set: (value: number | null) => {
    parametersForm.update({
      [pumpWearHeadCorrectionRatioField.value]: value ?? 1
    } as Partial<typeof parametersForm.data.value>);
  }
});

const pumpWearBhpCorrectionRatio = computed({
  get: () =>
    (getPumpFieldValue(
      parameters.value,
      "pumpWearBhpCorrectionRatio",
      props.stack
    ) as number | null | undefined) ?? 1,
  set: (value: number | null) => {
    parametersForm.update({
      [pumpWearBhpCorrectionRatioField.value]: value ?? 1
    } as Partial<typeof parametersForm.data.value>);
  }
});

// Helper functions to handle null values from ConverterInput
const setPumpWearHeadCorrectionRatio = (value: number | null) => {
  pumpWearHeadCorrectionRatio.value = value ?? 1;
};

const setPumpWearBhpCorrectionRatio = (value: number | null) => {
  pumpWearBhpCorrectionRatio.value = value ?? 1;
};

function valueChanged(
  current: number | null | undefined,
  next: number | null | undefined,
  epsilon = 1e-6
): boolean {
  if (current == null && next == null) {
    return false;
  }
  if (current == null || next == null) {
    return true;
  }
  return Math.abs(current - next) > epsilon;
}

const handlePumpHzInput = (value: number | null) => {
  const changed = valueChanged(pumpHz.value, value);
  pumpHz.value = value;
  if (changed) {
    markCalcHzDirtyFromManualInput();
  }
};

const handlePumpWearHeadInput = (value: number | null) => {
  const nextValue = value ?? 1;
  const changed = valueChanged(pumpWearHeadCorrectionRatio.value, nextValue);
  setPumpWearHeadCorrectionRatio(value);
  if (changed) {
    markCalcHzDirtyFromManualInput();
  }
};

const handlePumpWearBhpInput = (value: number | null) => {
  const nextValue = value ?? 1;
  const changed = valueChanged(pumpWearBhpCorrectionRatio.value, nextValue);
  setPumpWearBhpCorrectionRatio(value);
  if (changed) {
    markCalcHzDirtyFromManualInput();
  }
};

const useLegacyGasHeadRerate = computed({
  get: () =>
    (getPumpFieldValue(
      parameters.value,
      "useLegacyGasHeadRerate",
      props.stack
    ) as boolean | null | undefined) ?? false,
  set: (value: boolean) => {
    parametersForm.update({
      [useLegacyGasHeadRerateField.value]: value
    } as Partial<typeof parametersForm.data.value>);
  }
});

const router = useRouter();
const wrapper = ref();
watchEffect(() => {
  if (router.currentRoute.value.hash === "#pumps") {
    nextTick(() => {
      if (wrapper.value) {
        wrapper.value.$el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }
    });
  }
});

const showCurve = ref(false);
const initialPumpXref = ref<XrefPumps[number]>(generatePumpXref());
const autoAddInProgress = ref(false);
const calcHzNeedsRefresh = ref(false);
const autoAddCooldownUntilMs = ref(0);

const isEspOnShore = computed(() => {
  return (
    scenarioForm?.data.value.pumping_type ===
    Sizing_Pumping_Type_Enum.EspOnShore
  );
});

// Returns if pump stack contains 1 or more compression pumps
const containsCompressionPump = computed(() => {
  if (!isEspOnShore.value) {
    return true;
  }
  return (
    pumps.value &&
    Object.values(pumps.value).some(
      pump => !pump.journal_deleted_at && pump.r_pump?.is_compression_pump
    )
  );
});

// Returns if scenario calcs should use compression with the pump calcs
// This gets automatically set to true if pump stack contains a compression pump
// User also has the ability to flip this on if no pumps in stack are compression
const useCompression = computed({
  get() {
    return hasCompressionPump.value || containsCompressionPump.value;
  },
  set(value) {
    if (!containsCompressionPump.value) {
      hasCompressionPump.value = value;
    }
  }
});

// Only update form for compression if pump stack contains a compression pump
// The decision to use compression for calcs is still up to the user
watch(
  containsCompressionPump,
  isCompressionPresent => {
    if (isCompressionPresent) {
      hasCompressionPump.value = isCompressionPresent;
    }
  },
  { immediate: true }
);
const pumpsOrder = computed<string[]>(() => {
  const orderKey =
    props.stack === "lower" ? "lower_pumps_order" : "pumps_order";
  const order = (equipment.value[orderKey] as string[] | undefined) || [];
  // ensure r_pump_xref exists before rendering list
  return order.filter((pumpId: string) => pumpsForm.data.value[pumpId]);
});

const isHpsScenario = computed(
  () => scenarioForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps
);

const pumpsOrderXrefs = computed<XrefPumps>(() =>
  pumpsOrder.value
    .map(id => pumpsForm.data.value[id])
    .filter((p): p is XrefPumps[number] => !!p)
);

watch(
  pumpsOrder,
  order => {
    if (order.length === 0) {
      initialPumpXref.value = generatePumpXref();
    }
  },
  { immediate: true }
);
const hasConfiguredPump = computed(() =>
  pumpsOrderXrefs.value.some(p => !!p?.r_pump?.id)
);

const isCalcHzStale = computed(() => {
  if (autoAddInProgress.value && !calcHzNeedsRefresh.value) {
    return false;
  }

  if (!hasConfiguredPump.value || pumpHz.value === null) {
    return false;
  }

  return calcHzNeedsRefresh.value;
});

function markCalcHzDirty(): void {
  // Ignore system churn while auto-add is active or just completed.
  if (autoAddInProgress.value || Date.now() < autoAddCooldownUntilMs.value) {
    return;
  }
  calcHzNeedsRefresh.value = true;
}

function markCalcHzDirtyFromManualInput(): void {
  // Manual edits should always surface stale warning immediately.
  autoAddInProgress.value = false;
  autoAddCooldownUntilMs.value = 0;
  calcHzNeedsRefresh.value = true;
}

const newPumpExists = computed(() => {
  // Check if there are no pumps at all
  if (pumpsOrder.value?.length === 0) {
    return true;
  }
  // Check if any pumps have null pump_id (indicating incomplete configuration)
  return pumpsOrder.value.some((pumpId: string) => {
    const pump = pumpsForm.data.value[pumpId];
    return pump && pump.pump_id === null;
  });
});

const gasHeadRerateRatioForStack = computed(() => {
  return scenarioCalculations.pumpCalculations.aggregate.gasHeadRerateRatio
    .value;
});

function generatePumpXref(): XrefPumps[number] {
  const isHps =
    scenarioForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  return {
    __typename: "scenario_equipment_xref_pumps",
    id: v4(),
    scenario_equipment_id: equipment.value?.id,
    pump_id: null,
    rate_correction_ratio: 1,
    head_correction_ratio: 1,
    brake_horsepower_correction_ratio: 1,
    total_stages: 0,
    length_feet: 0,
    weight_pounds: 0,
    housings_order: [],
    winning_revision_id: null,
    winning_revision_parent_id: null,
    winning_revision_created_at: "now()",
    winning_revision_updated_at: "now()",
    winning_revision_deleted_at: null,
    r_pump: null,
    r_xref_housings: [],
    use_high_strength_shaft: isHps
  } as XrefPumps[number];
}

function addPump(
  index: number,
  xrefPump?: XrefPumps[number]
): XrefPumps[number] {
  const pump = xrefPump || generatePumpXref();
  const pumpIndex = index < 0 ? 0 : index;
  const orderKey =
    props.stack === "lower" ? "lower_pumps_order" : "pumps_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(pumpIndex, 0, pump.id);
  pumpsForm.update({
    [pump.id]: pump
  });
  equipmentForm.update({
    [orderKey]: newOrder
  });
  markCalcHzDirty();
  return pump;
}

function removePump(pump: XrefPumps[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_pumps_order" : "pumps_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(index, 1);

  // Delete all housings on pump
  const pumpHousings: XrefHousings =
    pump.housings_order?.map((id: string) => housings.value[id]) || [];

  // loop over each housing for the pump that was deleted
  pumpHousings.forEach(housing => {
    // xref exists in db, soft delete
    if (housingsForm.initialData.value?.[housing.id]) {
      housingsForm.update({
        [housing.id]: {
          ...housing,
          journal_deleted_at: "now()"
        }
      });
    } else {
      // xref was created but not saved, wipe out
      delete housingsForm.data.value[housing.id];
    }
  });

  // xref exists in db, soft delete
  if (pumpsForm.initialData.value?.[pump.id]) {
    pumpsForm.update({
      [pump.id]: {
        ...pump,
        journal_deleted_at: "now()",
        housings_order: [],
        weight_pounds: 0
      }
    });
  }
  // xref was created but not saved, wipe out
  else {
    delete pumpsForm.data.value[pump.id];
  }

  equipmentForm.update({
    [orderKey]: newOrder
  });
  markCalcHzDirty();
}

function updatePump(pump: XrefPumps[number], index: number): void {
  const existingPump: XrefPumps[number] = pumps.value[pump.id];
  const hasChangedPump = existingPump
    ? pump.r_pump?.id !== existingPump?.r_pump?.id
    : false;
  const orderKey =
    props.stack === "lower" ? "lower_pumps_order" : "pumps_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );

  newOrder.splice(index, 1, pump.id);

  pumpsForm.update({
    [pump.id]: {
      ...pump,
      housings_order: hasChangedPump ? [] : pump.housings_order
    }
  });
  equipmentForm.update({
    [orderKey]: newOrder
  });
  markCalcHzDirty();
}

function getPump(id: string): XrefPumps[number] {
  return pumps.value[id];
}

const predictionErrors = computed(() =>
  errors.value.filter(e =>
    [
      "pip_range",
      "pump_outside_graphing_range",
      "target_rate_above_max_flow_rate"
    ].includes(e.name)
  )
);

const severityLevels = ["success", "info", "warn", "error", "catastrophe"];

const severityClass = computed(
  () => severityLevels[Math.max(...errors.value.map(err => err.severity))]
);

const predictionErrorTooltipData = computed(() => ({
  value:
    predictionErrors.value[0]?.message +
    (predictionErrors.value.length > 1
      ? ` and ${predictionErrors.value.length - 1} more...`
      : ""),
  disabled: !predictionErrors.value[0],
  class: severityClass.value
}));

const disablePredictions = computed(() => {
  return (
    // Keep Calc Hz/Flow available even if we appear to already be within
    // pressure tolerance. During pump swaps/housing auto-add, scenario
    // calculations can lag briefly; disabling based on stale tolerance can
    // prevent Hz updates from ever running.
    !!predictionErrors.value.length ||
    pumpsOrder.value.length === 0 ||
    isNaN(scenarioCalculations.maxFlowRateAtIntakeBBLD.value)
  );
});

const isWithinHzPressureExitTolerance = computed(() => {
  const producedPressure =
    scenarioCalculations.pumpCalculations.aggregate
      .producedDischargePressurePSIA.value;
  const requiredPressure =
    scenarioCalculations.pumpCalculations.aggregate
      .requiredDischargePressurePSIA.value;

  if (
    !Number.isFinite(producedPressure) ||
    !Number.isFinite(requiredPressure)
  ) {
    return false;
  }

  return (
    Math.abs(producedPressure - requiredPressure) <= 1 &&
    producedPressure >= requiredPressure
  );
});

const disableCalcHzButton = computed(() => {
  return disablePredictions.value || isWithinHzPressureExitTolerance.value;
});

const casingFrictionLossTooltipText = ref(
  "Keep this box checked if the fluid casing friction losses were not included in the well test input"
);

type targetRowData = HypotheticalFlowDataRow | HypotheticalFrequencyDataRow;
function getDifferenceMessage(
  targetRow: targetRowData,
  messageType: string
): string {
  const { getPressureUnit } = useScenarioUnits(scenario);
  const targetPressureUnit = getPressureUnit();
  const produced = convert(
    targetRow.producedPressurePSIA,
    PoundsPerSquareInchAbsolute,
    targetPressureUnit.value
  );
  const required = convert(
    targetRow.requiredPressurePSIA,
    PoundsPerSquareInchAbsolute,
    targetPressureUnit.value
  );
  const pressureDifference = produced - required;

  const message = `Calc ${messageType} Accuracy within ${pressureDifference.toFixed(2)} ${targetPressureUnit.value.shortLabel}`;
  return message;
}
async function calcFlow() {
  emit("update:disabled", true);

  const calcs = getPredictedDesiredFlowRateSTBD(
    scenarioCalculations,
    parameters.value
  );
  const { status, targetRow } = calcs;
  const hypotheticalFlowRateSTBD = targetRow?.hypotheticalFlowRateSTBD || 0;

  if (status === "result-found" || status === "threshold-found") {
    // Ensure we only assign a number with 6 decimal places to the form
    const roundedFlowRate =
      Math.round(hypotheticalFlowRateSTBD * 10 ** 6) / 10 ** 6;
    parametersForm.update({
      target_liquid_rate_std_barrel_day: roundedFlowRate
    });

    const message = getDifferenceMessage(targetRow, "Flow");
    fireToast({
      severity: "success",
      detail: message,
      life: 7000
    });
  } else {
    const messageMap: {
      status: HypotheticalFlowResultStatus;
      message: string;
    }[] = [
      {
        status: "error: cannot find healthy flow",
        message: "Unable to find healthy flow"
      },
      {
        status: "error: cannot find healthy flow within tolerance",
        message: "Unable to find healthy flow within tolerance"
      }
    ];
    const message =
      messageMap.find(mm => mm.status === status)?.message ??
      "Unable to find target flow rate";

    fireToast({
      severity: "error",
      detail: message
    });
  }
  emit("update:disabled", false);
}

async function calcHz(
  options?: { suppressToast?: boolean } | MouseEvent
): Promise<number | null> {
  const suppressToast =
    !!options && typeof options === "object" && "suppressToast" in options
      ? !!options.suppressToast
      : false;
  emit("update:disabled", true);

  const calcs = getPredictedDesiredFrequencyHz(
    scenarioCalculations,
    parameters.value
  );
  const { status, targetRow } = calcs;

  if (status === "result-found" || status === "threshold-found") {
    const hypotheticalFrequencyHz = targetRow?.hypotheticalFrequencyHz || 0;
    const foundHz = Math.round(hypotheticalFrequencyHz * 10 ** 6) / 10 ** 6;
    pumpHz.value = foundHz;
    calcHzNeedsRefresh.value = false;

    if (!suppressToast) {
      const message = getDifferenceMessage(targetRow, "Hz");
      fireToast({
        severity: "success",
        detail: message,
        life: 7000
      });
    }
  } else {
    const messageMap: {
      status: HypotheticalFrequencyResultStatus;
      message: string;
    }[] = [
      {
        status: "error: cannot find threshold hz",
        message: "Unable to find threshold hz"
      },
      {
        status: "error: cannot find tolerance hz",
        message: "Unable to find tolerance hz"
      }
    ];
    const message =
      messageMap.find(mm => mm.status === status)?.message ??
      "Unable to find pump hz";

    if (!suppressToast) {
      fireToast({
        severity: "error",
        detail: message
      });
    }
  }
  emit("update:disabled", false);

  return status === "result-found" || status === "threshold-found"
    ? (pumpHz.value as number | null)
    : null;
}

async function handleAutoAddHzApplied(payload: {
  pumpXrefId: string;
}): Promise<void> {
  // Wait until this pump's housings are reflected in form state so auto-add
  // changes do not get interpreted as manual staleness.
  const maxAttempts = 30; // ~3s
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 100));

    const pump = pumpsForm.data.value[payload.pumpXrefId];
    const housingCount = pump?.housings_order?.length || 0;
    if (housingCount > 0 || attempt === maxAttempts - 1) {
      calcHzNeedsRefresh.value = false;
      autoAddInProgress.value = false;
      break;
    }
  }
}

function handleAutoAddStarted(): void {
  autoAddInProgress.value = true;
  autoAddCooldownUntilMs.value = Date.now() + 2000;
  calcHzNeedsRefresh.value = false;
}

async function handleAutoAddFinished(): Promise<void> {
  autoAddInProgress.value = false;
  // Guard against trailing reactive updates after auto-add completion.
  await nextTick();
  autoAddCooldownUntilMs.value = Date.now() + 2000;
  calcHzNeedsRefresh.value = false;
}
</script>
<style lang="scss" scoped>
.pump-stages {
  min-height: 60px;
  &.toggle {
    padding: 0.5rem;
  }
}
@media only screen and (max-width: 750px) {
  .mobile-wrap {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.calc-hz-stale-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--yellow-700);
  font-size: 0.9rem;
}

:deep(.calc-hz-stale-input .p-inputtext),
:deep(.calc-hz-stale-input .p-inputnumber-input) {
  background-color: var(--surface-200);
  border-color: var(--yellow-500);
}
</style>
